import React from 'react';
import { get_formated_time } from '../../command_center/utils';

/**
 * Interface representing the details of why a validation run was aborted or auto-stopped.
 */
export interface StopInfo {
  /** The code indicating the reason for stopping (e.g. CRITICAL_COMMAND_FAILED) */
  stopReasonCode: string;
  /** Optional human-readable message describing the stop scenario */
  stopReasonMessage?: string;
  /** The operation/command ID where the execution stopped */
  stoppedAtOperationId?: string;
  /** The step index within the execution sequence where the failure occurred */
  stoppedAtEntryIndex?: number;
  /** The count of consecutive failed responses triggering this stop */
  consecutiveNoResponseCount?: number;
  /** Timestamp or formatted date/time when the run stopped */
  stoppedAt?: string | number;
  /** The failure handling mode applied (e.g. ABORT_ON_FAILURE) */
  appliedFailureHandling?: string;
  /** The command UID where execution stopped */
  stoppedAtCommandUid?: string;
  /** The name of the failed testcase */
  testCaseName?: string;
  // Enhanced properties from websocket events
  eventType?: string;
  failedTestCaseId?: string;
  failedTestCaseName?: string;
  failedCommandId?: string | number;
  failedCommandName?: string;
  validationFailReason?: string;
  testPlanId?: string;
}

interface StopWarningBannerProps {
  /** The detailed stop reason metadata */
  stopInfo: StopInfo;
  /** Optional failure handling override path */
  appliedFailureHandling?: string;
  /** Callback triggered when clicking the close button */
  onDismiss: () => void;
  /** If true, positions the card globally/fixed on the viewport, rather than absolute relative to parent container */
  isGlobal?: boolean;
  /** Optional callback to navigate to Reports page */
  onViewReport?: () => void;
}

const isUuid = (str?: string): boolean => {
  if (!str) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str);
};

const cleanMessage = (msg?: string): string => {
  if (!msg) return '';
  return msg
    .replace(/\s*\(?operationId=[a-f0-9-]{36}\)?/gi, '')
    .replace(/\s*\(?validation=[a-f0-9-]{36}\)?/gi, '')
    .replace(/\s*[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '')
    .trim();
};

/**
 * A reusable banner component displaying information when a validation execution
 * has been aborted or auto-stopped due to critical step failures.
 */
export const StopWarningBanner: React.FC<StopWarningBannerProps> = ({
  stopInfo,
  appliedFailureHandling,
  onDismiss,
  isGlobal = false,
  onViewReport,
}) => {
  const isSkipped = stopInfo.eventType === 'TEST_CASE_EXECUTION_SKIPPED';
  const isPartial = stopInfo.eventType === 'TEST_CASE_EXECUTION_PARTIALLY_COMPLETED';
  const isWarning = isSkipped || isPartial;
  const iconColor = isWarning ? '#f59e0b' : '#FF3333';
  const titleColor = isWarning ? '#fcd34d' : '#fca5a5';

  let alertTitle = 'Execution Alert';
  if (stopInfo.eventType === 'TEST_CASE_EXECUTION_ABORTED' || stopInfo.eventType === 'TEST_PLAN_EXECUTION_ABORTED') {
    alertTitle = 'Test Case Aborted';
  } else if (stopInfo.eventType === 'TEST_CASE_EXECUTION_SKIPPED') {
    alertTitle = 'Test Case Skipped';
  } else if (stopInfo.eventType === 'TEST_CASE_EXECUTION_PARTIALLY_COMPLETED') {
    alertTitle = 'Test Case Partially Executed';
  }

  const showTestCase = stopInfo.testCaseName && !isUuid(stopInfo.testCaseName);
  const showFailedTestCaseName = !!stopInfo.failedTestCaseName;
  const showCommand = (stopInfo.failedCommandName || stopInfo.stoppedAtOperationId) && !isUuid(stopInfo.failedCommandName || stopInfo.stoppedAtOperationId);
  const showTime = stopInfo.stoppedAt;

  const hasDetails = !!(
    showTestCase ||
    showFailedTestCaseName ||
    showCommand ||
    stopInfo.validationFailReason ||
    showTime
  );

  return (
    <div 
      className={`rp__stop-banner ${isGlobal ? 'rp__stop-banner--global' : ''} ${isWarning ? 'rp__stop-banner--skipped' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="rp__stop-banner__header">
        <span className="rp__stop-banner__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </span>
        <span className="rp__stop-banner__title" style={{ color: titleColor }}>{alertTitle}</span>
        <button
          className="rp__stop-banner__close-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title="Dismiss alert"
          aria-label="Dismiss alert"
          type="button"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <div className="rp__stop-banner__body">
        {hasDetails && (
          <div className="rp__stop-banner__details">
            {showTestCase && (
              <>
                <span className="label">Test Case:</span>
                <span className="value">{stopInfo.testCaseName}</span>
              </>
            )}
            {showFailedTestCaseName && (
              <>
                <span className="label">Failed Test Case:</span>
                <span className="value">{stopInfo.failedTestCaseName}</span>
              </>
            )}
            {showCommand && (
              <>
                <span className="label">{isPartial ? 'Critical Command:' : 'Command:'}</span>
                <span className="value">
                  {stopInfo.failedCommandId != null && !isUuid(String(stopInfo.failedCommandId)) ? `[${stopInfo.failedCommandId}] ` : ''}
                  {stopInfo.failedCommandName || stopInfo.stoppedAtOperationId}
                </span>
              </>
            )}
            {stopInfo.validationFailReason && (
              <>
                <span className="label">Failure Reason:</span>
                <span className="value">{cleanMessage(stopInfo.validationFailReason)}</span>
              </>
            )}
            {showTime && (
              <>
                <span className="label">{isSkipped ? 'Skipped At:' : isPartial ? 'Failed At:' : 'Stopped At:'}</span>
                <span className="value">
                  {(() => {
                    try {
                      const epochMs = Number(stopInfo.stoppedAt);
                      const epochSec = epochMs > 1000000000000 ? epochMs / 1000 : epochMs;
                      return get_formated_time(epochSec);
                    } catch {
                      return String(stopInfo.stoppedAt);
                    }
                  })()}
                </span>
              </>
            )}
          </div>
        )}
        {onViewReport && (
          <button
            className="rp__stop-banner__action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onViewReport();
            }}
          >
            Go to Reports
          </button>
        )}
      </div>
    </div>
  );
};
